import { useMemo } from 'react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { Badge } from '@/components/data-display/Badge';
import type { ConveraApiLogDto } from './types';
import { getStatusVariant, getDirectionDisplay } from './types';

interface LogTableProps {
  logs: ConveraApiLogDto[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onRowClick: (log: ConveraApiLogDto) => void;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function LogTable({
  logs,
  isLoading,
  sorting,
  onSortingChange,
  onRowClick,
  total,
  page,
  limit,
  onPageChange,
  onPageSizeChange,
}: LogTableProps) {
  const columns = useMemo<ColumnDef<ConveraApiLogDto, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Timestamp',
        cell: ({ getValue }) => {
          const raw = getValue() as string;
          return (
            <span className="whitespace-nowrap font-mono text-[11px] text-[#666]">
              {new Date(raw).toLocaleString(undefined, {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          );
        },
      },
      {
        accessorKey: 'direction',
        header: 'Direction',
        enableSorting: false,
        cell: ({ getValue }) => {
          const dir = getDirectionDisplay(getValue() as string);
          return (
            <Badge variant={dir.variant}>
              {dir.icon} {dir.label}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'request_method',
        header: 'Method',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="text-[11px] font-semibold">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'request_url',
        header: 'Endpoint',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span
            className="max-w-[250px] truncate font-mono text-[11px] text-[#1a3a5c]"
            title={getValue() as string}
          >
            {getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'resource_id',
        header: 'Instruction',
        enableSorting: false,
        cell: ({ getValue }) => {
          const val = getValue() as string | null | undefined;
          return <span className="font-mono text-[11px]">{val ?? '\u2014'}</span>;
        },
      },
      {
        accessorKey: 'response_status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue() as number | null;
          const variant = getStatusVariant(status);
          return <Badge variant={variant}>{status ?? '—'}</Badge>;
        },
      },
      {
        accessorKey: 'duration_ms',
        header: 'Duration',
        cell: ({ getValue }) => {
          // `=== null`, not `??`: 0 ms is a legitimate render for sub-millisecond
          // outbound calls; only `null` (inbound webhooks have no round-trip
          // timing) collapses to the em-dash placeholder.
          const ms = getValue() as number | null;
          return (
            <span className="font-mono text-[11px] text-[#666]">
              {ms === null ? '—' : `${ms} ms`}
            </span>
          );
        },
      },
      {
        accessorKey: 'error_message',
        header: 'Error',
        enableSorting: false,
        cell: ({ getValue }) => {
          const err = getValue() as string | null | undefined;
          if (!err) return <span className="text-[#ccc]">{'\u2014'}</span>;
          return (
            <span className="max-w-[200px] truncate text-[11px] text-[#c62828]" title={err}>
              {err}
            </span>
          );
        },
      },
    ],
    [],
  );

  if (!isLoading && logs.length === 0) {
    return (
      <div
        className="rounded-[10px] bg-white p-12 text-center shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
        data-testid="logs-empty-state"
      >
        <p className="text-[14px] text-[#999]">No Convera logs found.</p>
      </div>
    );
  }

  return (
    <div data-testid="logs-table">
      <DataTable
        columns={columns}
        data={logs}
        sorting={sorting}
        onSortingChange={onSortingChange}
        pagination={{ pageIndex: page, pageSize: limit }}
        onPaginationChange={({ pageIndex, pageSize }) => {
          if (pageSize !== limit) {
            onPageSizeChange(pageSize);
          } else {
            onPageChange(pageIndex);
          }
        }}
        totalRows={total}
        isLoading={isLoading}
        rowClick={onRowClick}
      />
    </div>
  );
}
